/**
 * vedaManagerRoles.ts — Task 7 (2026-06-19), Phase 3 (2026-06-19)
 *
 * Shared source of truth for Veda Agro manager roles:
 *   • sales_manager     → lands on /veda-agro/sales  (Deal Desk / Sales Manager Dashboard)
 *   • logistics_manager → lands on /veda-agro/logistics (Logistics Cockpit)
 *   • purchase_head     → lands on /veda-agro/cockpit (Purchase Cockpit)
 *
 * Also re-exports the existing strict-view roles so RoleAwareHome and
 * RoleAwareDashboard in App.tsx can import a single function instead of
 * duplicating the if-chain.
 *
 * WIDE BUNDLE: all three roles are intentionally broad — 1–2 operators each
 * hold many hats. No per-step scope lockout.
 *
 * D-09 rule: no tenant slugs or IDs here.
 */
import { asLogisticsRole } from './roleAliases';

/**
 * Map a role.name to a hard-redirect destination.
 * Returns null if the role should use the default /dashboard landing.
 *
 * Order matters — more-specific checks first.
 */
export function resolveVedaManagerHome(roleName: string): string | null {
  // 2026-10-07 (logistics tour bug 20): logistics_head / LOGISTICS land like logistics_manager.
  switch (asLogisticsRole(roleName) === 'logistics_manager' ? 'logistics_manager' : roleName) {
    case 'lead_triage_coordinator': return '/leads/triage';
    case 'purchase':               return '/veda-agro/cockpit';
    case 'purchase_manager':       return '/veda-agro/cockpit';
    case 'purchase_head':          return '/veda-agro/cockpit';
    case 'sales_manager':          return '/veda-agro/sales';
    // 2026-09-21 (Manak): sales reps land on the Trading Dashboard too.
    case 'sales_rep':              return '/veda-agro/sales';
    case 'logistics_manager':      return '/veda-agro/logistics';
    // 2026-09-25 (Manak): accountant lands on the Accounts Cockpit.
    case 'accountant':              return '/veda-agro/accounts';
    // 2026-09-26 (Manak, decision 2a): admin + owner land on the Admin Dashboard —
    // the generic dashboard reads tables Veda never fills.
    case 'admin':                  return '/veda-agro/admin';
    case 'owner':                  return '/veda-agro/admin';
    default:                       return null;
  }
}

/**
 * Minimal nav-item shape for use in VEDA_MANAGER_NAV.
 * Mirrors the NavItem interface in Sidebar.tsx — we keep it independent
 * so this file stays importable from pure-TS tests without React.
 */
export interface VedaNavItem {
  to: string;
  label: string;
  module?: string;
  count?: number;
}

export interface VedaNavSection {
  label: string;
  items: VedaNavItem[];
}

/**
 * TaraWorld-shaped nav groups for the two Veda manager roles.
 * Each entry maps to an EXISTING route — no new routes are added here.
 *
 * Groups (from spec):
 *   Commodity Deals: Purchase Cockpit, Deal Desk ⌘D, Allocation Board, Routing
 *   Market:          Rate Book, Trade Network·Book, Requirements, Quotes,
 *                    Margin Desk, Contracts, Truck Tracking
 *
 * sales_manager → deal/market sales subset
 * logistics_manager → dispatch/lanes/truck subset
 *
 * SOURCE-OF-TRUTH NOTE: Sidebar.tsx section arrays (VEDA_SALES_MANAGER_SECTIONS /
 * VEDA_LOGISTICS_MANAGER_SECTIONS) are the rendered nav. VEDA_MANAGER_NAV below is a
 * test/reference mirror — keep the two in sync to avoid silent drift.
 */
export const VEDA_MANAGER_NAV: Record<
  'sales_manager' | 'logistics_manager' | 'purchase_head',
  VedaNavSection[]
> = {
  sales_manager: [
    {
      label: 'Commodity Deals',
      items: [
        { to: '/veda-agro/cockpit',   label: 'Purchase Cockpit', module: 'veda_commodity_deals' },
        { to: '/veda-agro/sales',     label: 'Deal Desk ⌘D',    module: 'veda_commodity_deals' },
        { to: '/veda-agro/work-plan', label: 'Allocation Board', module: 'veda_commodity_deals' },
        { to: '/veda-agro/triage',    label: 'Routing',          module: 'veda_commodity_deals' },
      ],
    },
    {
      label: 'Market',
      items: [
        { to: '/veda-agro/rate-update',  label: 'Rate Book',         module: 'veda_commodity_deals' },
        { to: '/veda-agro/directory',    label: 'Trade Network·Book', module: 'veda_commodity_deals' },
        { to: '/veda-agro/enquiries',    label: 'Requirements',       module: 'veda_commodity_deals' },
        { to: '/veda-agro/approvals',    label: 'Margin Desk',        module: 'veda_commodity_deals' },
        { to: '/veda-agro/orders',       label: 'Contracts',          module: 'veda_commodity_deals' },
      ],
    },
  ],

  logistics_manager: [
    {
      label: 'Commodity Deals',
      items: [
        { to: '/veda-agro/cockpit',   label: 'Purchase Cockpit', module: 'veda_commodity_deals' },
        { to: '/veda-agro/work-plan', label: 'Allocation Board', module: 'veda_commodity_deals' },
      ],
    },
    {
      label: 'Dispatch & Lanes',
      items: [
        { to: '/veda-agro/logistics',    label: 'Logistics Cockpit',  module: 'veda_commodity_deals' },
        { to: '/veda-agro/rate-update',  label: 'Rate Book',          module: 'veda_commodity_deals' },
        { to: '/veda-agro/directory',    label: 'Trade Network·Book', module: 'veda_commodity_deals' },
        { to: '/veda-agro/enquiries',    label: 'Requirements',       module: 'veda_commodity_deals' },
        { to: '/veda-agro/orders',       label: 'Contracts',          module: 'veda_commodity_deals' },
      ],
    },
    // G3 mirror: "Truck Tracking" Market group removed — it duplicated /veda-agro/logistics
    // (same as Logistics Cockpit in Dispatch & Lanes). Mirrors Sidebar.tsx fix.
  ],

  purchase_head: [
    {
      label: 'Purchase',
      items: [
        { to: '/veda-agro/cockpit',   label: 'Purchase Cockpit', module: 'veda_commodity_deals' },
        { to: '/veda-agro/enquiries', label: 'Deal Desk',        module: 'veda_commodity_deals' },
        { to: '/veda-agro/work-plan', label: 'Allocation Board', module: 'veda_commodity_deals' },
        // 2026-06-22 (Manak): Orders (view+create SO & PO) + Stage Tracker (view-only).
        { to: '/veda-agro/orders',        label: 'Orders',        module: 'veda_commodity_deals' },
        { to: '/veda-agro/stage-tracker', label: 'Dispatch Tracker', module: 'veda_commodity_deals' },
        // Mapping Manager — vendor↔commodity master-data (purchase desk).
        { to: '/veda-agro/mapping',       label: 'Mapping',       module: 'veda_commodity_deals' },
      ],
    },
  ],
};
